import type {
  HTMLAttributes,
} from 'react'

import {
  Skeleton,
  SkeletonGroup,
} from './skeleton'

import { cn } from '@/shared/lib/cn'

export interface SkeletonTableProps
  extends HTMLAttributes<HTMLDivElement> {
  rows?: number
  columns?: number
  showHeader?: boolean
  loadingLabel?: string
}

export function SkeletonTable({
  rows = 5,
  columns = 5,
  showHeader = true,
  loadingLabel = 'Loading table',
  className,
  ...props
}: SkeletonTableProps) {
  const safeRowCount =
    Math.max(1, Math.floor(rows))

  const safeColumnCount =
    Math.max(1, Math.floor(columns))

  return (
    <SkeletonGroup
      {...props}
      label={loadingLabel}
      className={cn(
        [
          'overflow-hidden rounded-xl',
          'border',
          'border-(--color-border)',
          'bg-(--color-surface)',
        ],
        className,
      )}
    >
      {showHeader ? (
        <div
          className={[
            'grid gap-4',
            'border-b',
            'border-(--color-border)',
            'bg-(--color-surface-muted)',
            'px-6 py-4',
          ].join(' ')}
          style={{
            gridTemplateColumns:
              `repeat(${safeColumnCount}, minmax(0, 1fr))`,
          }}
        >
          {Array.from({
            length: safeColumnCount,
          }).map((_, columnIndex) => (
            <Skeleton
              key={columnIndex}
              shape="text"
              className="h-3 w-20 max-w-full"
            />
          ))}
        </div>
      ) : null}

      <div>
        {Array.from({
          length: safeRowCount,
        }).map((_, rowIndex) => (
          <div
            key={rowIndex}
            className={[
              'grid gap-4 px-6 py-4',
              'border-b',
              'border-(--color-border)',
              'last:border-b-0',
            ].join(' ')}
            style={{
              gridTemplateColumns:
                `repeat(${safeColumnCount}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({
              length: safeColumnCount,
            }).map(
              (_, columnIndex) => (
                <Skeleton
                  key={columnIndex}
                  shape="text"
                  className={cn(
                    'h-3',
                    columnIndex === 0
                      ? 'w-24'
                      : 'w-20',
                  )}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </SkeletonGroup>
  )
}